"use client";

import { GE } from ".constants/gtmEvents";
import { PORTALS } from ".constants/portals";
import {
  PortalModal,
  ModalHeader,
  ModalContent,
  Icon,
  usePortalContext,
  Button,
  HR,
} from "@cloai/uikit";
import { useTranslations } from "next-intl";
import { memo } from "react";

import { useDropzoneSettings } from "@/components/user-flow/utils/useDropzoneSettings";
import { useGTMContext, useUndressContext } from "@/providers";
import { cn } from "@/utils/cn";

import { STYLES } from "./vars";

const cardStyles = {
  dropzone: {
    base: cn(
      "group/item relative flex aspect-[1/2] size-full cursor-pointer flex-col items-center justify-center gap-5 overflow-hidden rounded-xl bg-regular duration-300 select-none",
      "border-3 border-dashed border-primary p-5",
      "@lg:aspect-[2/1]",
      "after:absolute after:size-full after:bg-card/20",
      "hover:bg-[oklch(from_var(--color-regular)_calc(l_+_0.1)_c_h)]",
      "active:scale-98 active:bg-[oklch(from_var(--color-regular)_calc(l_+_0.05)_c_h)] active:duration-100",
    ),
    isDragActive: cn(
      "border-solid bg-[oklch(from_var(--color-regular)_calc(l_+_0.1)_c_h)]",
    ),
    loading: cn("pointer-events-none opacity-80"),
  },

  image: cn(
    "pointer-events-none block size-full object-contain object-bottom duration-300",
    "group-hover/item:scale-103 group-hover/item:duration-800",
  ),
  text: cn(
    "relative z-1 text-center text-2xl font-bold text-balance uppercase text-shadow-md",
    "md:text-2xl",
  ),
};

export const UploadPortal = memo(() => {
  const t = useTranslations("HomePage");
  const { loading } = useUndressContext();
  const { openPortal, closePortal } = usePortalContext();
  const { sendGTM } = useGTMContext();

  const {
    maxFilesPhoto,
    getRootPhotoProps,
    getInputPhotoProps,
    isDragPhotoActive,
  } = useDropzoneSettings();

  const onUploadUrl = () => {
    sendGTM({ event: GE.FLOW_START_CLICK_UPLOAD_URL });
    closePortal();
    openPortal(PORTALS.ATTACH_PHOTO_BY_URL);
  };

  return (
    <PortalModal
      portalId={PORTALS.USER_FLOW_UPLOAD}
      style={STYLES.portal.variants.medium}
    >
      <ModalHeader>UPLOAD PHOTOS</ModalHeader>

      <ModalContent className="@container">
        <div
          className={cn(
            cardStyles.dropzone.base,
            isDragPhotoActive && cardStyles.dropzone.isDragActive,
            loading && cardStyles.dropzone.loading,
          )}
          {...getRootPhotoProps({})}
        >
          <input {...getInputPhotoProps()} />

          <Icon icon="attach_file" size={10} />

          <span
            className={cn(
              STYLES.text.base,
              STYLES.text.variants.small,
              "text-regular-foreground",
            )}
          >
            Drag & drop photo (max {maxFilesPhoto})
            <br />
            or click on area to <span className="text-primary">upload</span>
          </span>
        </div>

        <HR text="OR" />

        <Button
          startContent={<Icon icon="add_link" />}
          wide
          centerText
          onClick={onUploadUrl}
          loading={loading}
        >
          {t("photoByURL")}
        </Button>
      </ModalContent>
    </PortalModal>
  );
});

UploadPortal.displayName = "UploadPortal";
